import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { OrderDetailsModal } from './OrderDetailsModal';

export default function ManufacturerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-orders');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  const [statusFilter, setStatusFilter] = useState('');

  const statusSteps = [
    { value: 'pending', label: 'Pending', icon: '🔄', color: 'bg-purple-100 text-purple-800' },
    { value: 'confirmed', label: 'Confirmed', icon: '✅', color: 'bg-blue-100 text-blue-800' },
    { value: 'assigned', label: 'Assigned', icon: '👨‍🏭', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'processing', label: 'Processing', icon: '🎨', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'printed', label: 'Printed', icon: '🖨️', color: 'bg-orange-100 text-orange-800' },
    { value: 'quality_check', label: 'Quality Check', icon: '🔍', color: 'bg-amber-100 text-amber-800' },
    { value: 'packed', label: 'Packed', icon: '📦', color: 'bg-teal-100 text-teal-800' },
    { value: 'shipped', label: 'Shipped', icon: '🚚', color: 'bg-green-100 text-green-800' },
    { value: 'delivered', label: 'Delivered', icon: '🏁', color: 'bg-emerald-100 text-emerald-800' },
    { value: 'completed', label: 'Completed', icon: '🎉', color: 'bg-green-100 text-green-800' },
    { value: 'cancelled', label: 'Cancelled', icon: '❌', color: 'bg-red-100 text-red-800' }
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchOrders();
  }, [user, navigate, activeTab, pagination.page, statusFilter]);

  const fetchOrders = async () => {
  try {
    setLoading(true);
    
    if (activeTab === 'my-orders') {
      const { data } = await api.get('/manufacturers/my-orders', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          status: statusFilter
        }
      });
      
      setOrders(data.data);
      setPagination({
        page: data.pagination.page,
        limit: data.pagination.limit,
        total: data.pagination.total,
        pages: data.pagination.pages
      });
    } else if (activeTab === 'available') {
      const { data } = await api.get('/manufacturers/available-orders');
      setAvailableOrders(data.data.orders);
    }
  } catch (err) {
    console.error('Error fetching orders:', err);
    toast.error('Failed to load orders');
  } finally {
    setLoading(false);
  }
};

  const handleAcceptOrder = async (orderId) => {
    try {
      const { data } = await api.post(`/manufacturers/accept-order/${orderId}`);
      toast.success('Order accepted successfully!');
      setAvailableOrders(prev => prev.filter(order => order._id !== orderId));
      setOrders(prev => [data.data.order, ...prev]);
      setPagination(prev => ({
        ...prev,
        total: prev.total + 1
      }));
    } catch (err) {
      console.error('Error accepting order:', err);
      toast.error(err.response?.data?.message || 'Failed to accept order');
    }
  };

    const handleUpdateStatus = async (orderId, status) => {
  try {
    const { data } = await api.put(`/manufacturers/orders/${orderId}/status`, { 
      status,
      notes: `Status changed to ${status}`
    });
    
    toast.success(`Status updated to ${statusSteps.find(s => s.value === status)?.label || status}`);
    
    setOrders(prev => prev.map(order => 
      order._id === orderId ? { ...order, ...data.data } : order
    ));
    
    setSelectedOrder(prev => prev ? { ...prev, ...data.data } : null);
  } catch (err) {
    console.error('Error updating status:', err);
    if (err.response?.status === 404 && err.response?.data?.message === 'Manufacturer profile not found') {
      // Handle case where manufacturer profile is missing
      toast.error('Please complete your manufacturer profile first');
      navigate('/manufacturer/profile');
    } else {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  }
};

    const handleViewDetails = (order) => {
        setSelectedOrder(order);
        setIsModalOpen(true);
    };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusColor = (status) => {
    const step = statusSteps.find(s => s.value === status);
    return step ? step.color : 'bg-gray-100 text-gray-800';
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const getProductImage = (product) => {
    if (product?.images?.length > 0) {
      return product.images[0];
    }
    if (product?.design) {
      return product.design;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }
  
      console.log(orders);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">Manufacturer Dashboard</h1>
            <p className="mt-2 text-lg text-gray-600">Manage your manufacturing orders</p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <button 
              onClick={() => navigate('/manufacturer/profile')}
              className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-all"
            >
              View Profile
            </button>
            <button 
              onClick={() => navigate('/manufacturer/analytics')}
              className="px-6 py-2 border border-black text-black rounded-full hover:bg-gray-100 transition-all"
            >
              Analytics
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => {
                setActiveTab('my-orders');
                setStatusFilter('');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'my-orders' ? 'border-b-2 border-pink-500 text-pink-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              My Orders
            </button>
            <button
              onClick={() => {
                setActiveTab('available');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'available' ? 'border-b-2 border-pink-500 text-pink-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Available Orders
            </button>
          </div>
        </div>

        {/* Status Filter for My Orders */}
        {activeTab === 'my-orders' && (
          <div className="mb-6 flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter('')}
              className={`px-3 py-1 rounded-full text-xs font-medium ${!statusFilter ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-800'}`}
            >
              All
            </button>
            {statusSteps.map(status => (
              <button
                key={status.value}
                onClick={() => setStatusFilter(status.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium ${statusFilter === status.value ? status.color : 'bg-gray-200 text-gray-800'}`}
              >
                {status.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'my-orders' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            {orders.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-5xl mb-4">📭</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
                <p className="text-gray-500">
                  {statusFilter 
                    ? `You don't have any ${statusFilter.replace('_', ' ')} orders`
                    : "You haven't accepted any orders yet"}
                </p>
                {!statusFilter && (
                  <button
                    onClick={() => setActiveTab('available')}
                    className="mt-4 px-4 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700"
                  >
                    View Available Orders
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="divide-y divide-gray-200">
                  {orders.map(order => (
                    <div key={order._id} className="p-4 sm:p-6 hover:bg-gray-50">
                      <div className="flex flex-col sm:flex-row justify-between">
                        <div className="mb-4 sm:mb-0">
                          <div className="flex items-center">
                            <span className="text-lg mr-2">📦</span>
                            <h3 className="text-lg font-medium text-gray-900">
                              Order #{order.orderNumber}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            From {order.store?.name || 'Store'} • Placed on {formatDate(order.createdAt)}
                          </p>
                          {order.buyer && (
                            <p className="text-sm text-gray-500">
                              Buyer: {order.buyer.firstName} {order.buyer.lastName}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {statusSteps.find(s => s.value === order.status)?.label || order.status}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="flex overflow-x-auto pb-2 -mx-2">
                          {order.items.slice(0, 4).map(item => {
                            const image = getProductImage(item.product);
                            return (
                              <div key={item._id} className="flex-shrink-0 px-2">
                                <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                                  {image ? (
                                    <img 
                                      src={image} 
                                      alt={item.product?.name || 'Product'}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                      🛍️
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {order.items.length > 4 && (
                            <div className="flex-shrink-0 px-2">
                              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                <span className="text-gray-500">+{order.items.length - 4}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 flex justify-between items-center">
                        <button
                        onClick={() => handleViewDetails(order)}
                        className="px-4 py-2 border border-black text-black rounded-full hover:bg-gray-50 transition-all text-sm font-medium"
                        >
                            View Details
                        </button>
                        <div className="text-sm text-gray-500">
                          {order.items.reduce((sum, item) => sum + item.quantity, 0)} items • 
                          {order.items.map(item => ` ${item.printMethod}`).join(', ')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.pages}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                          <span className="font-medium">
                            {Math.min(pagination.page * pagination.limit, pagination.total)}
                          </span>{' '}
                          of <span className="font-medium">{pagination.total}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                          >
                            <span className="sr-only">Previous</span>
                            &larr;
                          </button>
                          {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                            let pageNum;
                            if (pagination.pages <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.page >= pagination.pages - 2) {
                              pageNum = pagination.pages - 4 + i;
                            } else {
                              pageNum = pagination.page - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  pagination.page === pageNum
                                    ? 'z-10 bg-pink-600 border-pink-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page === pagination.pages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                          >
                            <span className="sr-only">Next</span>
                            &rarr;
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'available' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            {availableOrders.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-5xl mb-4">🎉</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No available orders</h3>
                <p className="text-gray-500">All current orders have been assigned.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {availableOrders.map(order => (
                  <div key={order._id} className="p-4 sm:p-6 hover:bg-gray-50">
                    <div className="flex flex-col sm:flex-row justify-between">
                      <div className="mb-4 sm:mb-0">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">📦</span>
                          <h3 className="text-lg font-medium text-gray-900">
                            Order #{order.orderNumber}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          From {order.store?.name || 'Store'} • Placed on {formatDate(order.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {order.status}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex overflow-x-auto pb-2 -mx-2">
                        {order.items.slice(0, 4).map(item => {
                          const image = getProductImage(item.product);
                          return (
                            <div key={item._id} className="flex-shrink-0 px-2">
                              <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                                {image ? (
                                  <img 
                                    src={image} 
                                    alt={item.product?.name || 'Product'}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    🛍️
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {order.items.length > 4 && (
                          <div className="flex-shrink-0 px-2">
                            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                              <span className="text-gray-500">+{order.items.length - 4}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 flex justify-between items-center">
                      <button
                        onClick={() => handleAcceptOrder(order._id)}
                        className="px-4 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition-all text-sm font-medium"
                      >
                        Accept Order
                      </button>
                      <div className="text-sm text-gray-500">
                        {order.items.reduce((sum, item) => sum + item.quantity, 0)} items • 
                        {order.items.map(item => ` ${item.printMethod}`).join(', ')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {isModalOpen && selectedOrder && (
        <OrderDetailsModal
            order={selectedOrder}
            onClose={() => setIsModalOpen(false)}
            onStatusUpdate={handleUpdateStatus}
        />
       )}
    </div>
  );
}